<script setup lang="ts">
import Pack from '@/assets/packs/004.png';
import CardIcon from '@/assets/packs/cards_black.png';
import { ref, type Ref, watch, onMounted } from 'vue';

import ConfettiExplosion from 'vue-confetti-explosion';
import BootsLogo from '@/assets/logos/boots.svg';

import { PublicKey } from '@solana/web3.js';
import { Http } from '@raindrops-protocol/raindrops';
import * as anchor from '@coral-xyz/anchor';
import { useAnchorWallet } from 'solana-wallets-vue';
import {
    type SignerWalletAdapterProps,
    // WalletSignTransactionError,
} from '@solana/wallet-adapter-base';

import { PACK_ITEM_CLASS, ENV, BUILD_SECRET } from '../helpers/constants';
import { goTo } from '@/helpers/utilities';
import { getPacksRemaining } from '@/helpers/solana';
import HaramebayLogo from './Items/HaramebayLogo.vue';

import { useRouter } from 'vue-router';

interface AnchorWallet {
    publicKey: PublicKey;
    signTransaction: SignerWalletAdapterProps['signTransaction'];
    signAllTransactions: SignerWalletAdapterProps['signAllTransactions'];
}

// Add ref for auction link.
// const props = defineProps<{ auction?: string | null }>();
const showOpalText: Ref<boolean> = ref(false);
const opalIsLoaded: Ref<boolean> = ref(false);
const bgIsLoaded: Ref<boolean> = ref(false);
const computerIsLoaded: Ref<boolean> = ref(false);
const loader: Ref<boolean> = ref(true);
const packBuyModal: Ref<boolean> = ref(false);
const txStatus: Ref<string> = ref('');
const showTxStatus: Ref<boolean> = ref(false);
const showLoader: Ref<boolean> = ref(false);
const showConfetti: Ref<boolean> = ref(false);
const packsRemaining: Ref<number> = ref(0);
const mintPackStart: Ref<number | null> = ref(null);
const timeToMintPack: Ref<number> = ref(0);
const countdownStarted: Ref<boolean> = ref(false);

const wallet: Ref<AnchorWallet | undefined> = useAnchorWallet();

const router = useRouter();

const startCountdown = () => {
    countdownStarted.value = true;
    if (mintPackStart.value) {
        setInterval(() => {
            timeToMintPack.value = mintPackStart.value! - new Date().valueOf();
        }, 1000);
    }
};

const getTimeToOpen = async () => {
    const bootsClient = new Http.Item.Client(
        new anchor.AnchorProvider(
            new anchor.web3.Connection(ENV.RPC),
            wallet.value!,
            {
                preflightCommitment: 'processed',
                commitment: 'confirmed',
            }
        ),
        ENV.STRING as anchor.web3.Cluster,
        ENV.RPC,
        ENV.BOOTS
    );
    try {
        const { live, startTime } = await bootsClient.buildIsLive(
            new PublicKey(PACK_ITEM_CLASS),
            [],
            BUILD_SECRET
        );
        if (live) {
            mintPackStart.value = 0;
        } else if (!live && startTime) {
            mintPackStart.value = startTime;
        }
        startCountdown();
    } catch (err) {
        console.log(err);
    }
};

const unixToTimeRemaining = (time: number) => {
    const h = Math.floor(time / 1000 / 60 / 60);
    const m = Math.floor(time / 1000 / 60) % 60;
    const s = Math.floor(time / 1000) % 60;
    return `${h.toString().padStart(2, `0`)}:${m
        .toString()
        .padStart(2, `0`)}:${s.toString().padStart(2, `0`)}`;
};

// Sticky scroll event listener.
let isTop = true;
let isScrolling = false;
window.addEventListener('scroll', () => {
    // Set the max container height.
    const limit =
        Math.max(
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.clientHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight
        ) / 2;

    // If not scrolling.
    if (!isScrolling) {
        if (isTop && window.scrollY > 40) {
            isScrolling = true;
            window.scrollTo({ top: limit, left: 0, behavior: `smooth` });
            isTop = false;
        } else if (!isTop && window.scrollY < limit - 40) {
            isScrolling = true;
            window.scrollTo({ top: 0, left: 0, behavior: `smooth` });
            isTop = true;
        }
    }

    // If already scrolling.
    else if (isScrolling) {
        if (isTop && window.scrollY < 20) {
            isScrolling = false;
        } else if (!isTop && window.scrollY > limit - 20) {
            isScrolling = false;
        }
    }
});

const goToAuction = () => {
    router.push(`/auction/s/ESaThLpnmXv6N5Tqng2giXSRdC87bquo3RSh59t3eiqn`);
};

const toggleOpalText = (state: boolean) => {
    showOpalText.value = state;
};

const purchasePack = async () => {
    return null;
};

const closePurchaseMessage = async () => {
    showConfetti.value = false;
    showTxStatus.value = false;
};

onMounted(() => {
    getPacksRemaining(new PublicKey(PACK_ITEM_CLASS)).then(
        (r) => (packsRemaining.value = r)
    );
    setInterval(() => {
        getPacksRemaining(new PublicKey(PACK_ITEM_CLASS)).then(
            (r) => (packsRemaining.value = r)
        );
    }, 10000);
    if (wallet.value?.publicKey) getTimeToOpen();
});

// Watch for items to load.
watch(
    [opalIsLoaded, bgIsLoaded, computerIsLoaded],
    async ([video, opal, computer]) => {
        if (video && opal && computer) loader.value = false;
    }
);

// Watch for wallet connect.
watch([wallet], async ([wallet]) => {
    if (wallet && wallet.publicKey && !countdownStarted.value) getTimeToOpen();
});
</script>

<template>
    <div :class="`loader-container ${!loader && `is-loaded`}`">
        <span class="loader"></span>
    </div>
    <div
        :style="{
            opacity: packBuyModal ? 1 : 0,
            pointerEvents: packBuyModal ? `all` : `none`,
        }"
        :class="`pb-modal`"
    >
        <div
            class="pb-modal-backdrop"
            @click="() => (packBuyModal = false)"
        ></div>
        <div
            :style="{
                opacity: showTxStatus ? 1 : 0,
                pointerEvents: showTxStatus ? `all` : `none`,
                transition: `opacity 0.25s ease`,
            }"
            class="pb-modal-transaction-status"
        >
            <span
                v-if="!showConfetti && showLoader"
                class="loader ldr-white"
            ></span>
            <div class="transaction-text">{{ txStatus }}</div>
            <div v-if="showConfetti" class="transaction-buttons">
                <div
                    @click="() => goTo(`https://itsboots.xyz/bootspacks`)"
                    class="pb-transaction-button"
                >
                    OPEN WITH
                    <img
                        style="
                            height: 24px;
                            margin-left: 8px;
                            padding-bottom: 3px;
                        "
                        :src="BootsLogo"
                    />
                </div>
                <div
                    @click="closePurchaseMessage"
                    class="pb-transaction-button"
                >
                    GIB MOAR PACKS!!!
                </div>
            </div>
            <div v-if="showConfetti">
                <ConfettiExplosion
                    :stageHeight="2000"
                    :stageWidth="3000"
                    :particleSize="20"
                    :duration="4000"
                    :particleCount="200"
                    :force="1"
                    :colors="[
                        '#98d455',
                        '#db3575',
                        '#5AB5E7',
                        '#32adf0',
                        '#4b57db',
                        '#dbd037',
                        '#fa723c',
                    ]"
                />
            </div>
        </div>
        <div
            :style="{
                opacity: packBuyModal && !showTxStatus ? 1 : 0,
                pointerEvents: packBuyModal && !showTxStatus ? `all` : `none`,
                transition: `opacity 0.25s ease`,
            }"
            class="pb-modal-container"
        >
            <div class="pb-modal-left">
                <img :style="{ height: `100%` }" :src="Pack" class="floating" />
            </div>
            <div class="pb-modal-right">
                <div class="pb-title-container">
                    <div class="pb-title-icon">
                        <img :src="CardIcon" />
                    </div>
                    <div class="pb-season-text">Season 3</div>
                    <div class="pb-title-text">NOTORIOUS A.P.E.</div>
                </div>
                <div class="pb-action-container">
                    <div class="pb-action-stat mobile-left">
                        <div class="pb-action-stat-title">Packs Remaining:</div>
                        <div class="pb-action-stat-value">
                            {{ 0 }}
                        </div>
                    </div>
                    <div class="pb-action-stat mobile-right">
                        <div class="pb-action-stat-title">Price:</div>
                        <div class="pb-action-stat-value">0.69 SOL</div>
                    </div>
                </div>
                <div
                    @click="
                        () =>
                            BUILD_SECRET ||
                            (wallet?.publicKey &&
                                packsRemaining > 0 &&
                                mintPackStart &&
                                timeToMintPack <= 0)
                                ? purchasePack()
                                : null
                    "
                    :class="`pb-action-button ${
                        BUILD_SECRET ||
                        (wallet?.publicKey &&
                            packsRemaining > 0 &&
                            mintPackStart &&
                            timeToMintPack <= 0)
                            ? ``
                            : `disabled`
                    }`"
                >
                    {{
                        wallet?.publicKey
                            ? timeToMintPack > 0
                                ? unixToTimeRemaining(timeToMintPack)
                                : `Purchase`
                            : `Not Connected`
                    }}
                </div>
                <div class="pb-contents-container">
                    You will receive <b>one</b> of the following:
                    <div class="pb-contents">
                        <img
                            style="border: 2px solid #73b6e9"
                            src="https://degenape.nyc3.cdn.digitaloceanspaces.com/apes/traits_v2/notorious_ape/reno_sheriff_shirt.png"
                        />
                        <img
                            style="border: 2px solid #e9bf64"
                            src="https://degenape.nyc3.cdn.digitaloceanspaces.com/apes/traits_v2/notorious_ape/flat_cap.png"
                        />
                        <img
                            style="border: 2px solid #e9bf64"
                            src="https://degenape.nyc3.cdn.digitaloceanspaces.com/apes/traits_v2/notorious_ape/british_gangster_coat.png"
                        />
                        <img
                            style="border: 2px solid #ef748a"
                            src="https://degenape.nyc3.cdn.digitaloceanspaces.com/apes/traits_v2/notorious_ape/versacape_shades.png"
                        />
                        <img
                            style="border: 2px solid #ef748a"
                            src="https://degenape.nyc3.cdn.digitaloceanspaces.com/apes/traits_v2/notorious_ape/biggie_leather_jacket.png"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="landing-panel flex-center">
        <div class="backend-assembly">
            <img
                class="smoke"
                src="@/assets/landing/purple.png"
                @load="() => (bgIsLoaded = true)"
            />
            <div class="mobile-shadow"></div>
            <img class="ape" src="@/assets/landing/ape_gold_bg.png" />
            <div class="apeintosh desktop" @click="goToAuction()">
                <img
                    src="@/assets/landing/apeintosh.png"
                    @load="() => (computerIsLoaded = true)"
                />
                <img
                    class="apeintosh-reflect"
                    src="@/assets/landing/screen.png"
                />
                <div class="apeintosh-screen"><HaramebayLogo /></div>
                <div class="apeintosh-shadow"></div>
            </div>
            <div
                @mouseenter="() => toggleOpalText(true)"
                @mouseleave="() => toggleOpalText(false)"
                class="opal-container"
            >
                <img
                    :style="{ height: `150%`, animation: `` }"
                    :src="Pack"
                    @click="() => (packBuyModal = true)"
                    @load="() => (opalIsLoaded = true)"
                    class="floating"
                />
                <div class="opal-shadow otx-shadow-wrapper"></div>
                <!-- <div :class="`opal-text ${showOpalText && `show-opal-text`}`">
                    <div class="opal-text-letter otx-a">
                        <div class="otx-anim-wrapper otx-anim-delay-1_0">g</div>
                    </div>
                    <div class="opal-text-letter otx-s1">
                        <div class="otx-anim-wrapper otx-anim-delay-0_4">i</div>
                    </div>
                    <div class="opal-text-letter otx-s2">
                        <div class="otx-anim-wrapper otx-anim-delay-0_6">b</div>
                    </div>
                    <div class="opal-text-letter otx-e">
                        <div class="otx-anim-wrapper otx-anim-delay-1_2">e</div>
                    </div>
                    <div class="opal-text-letter otx-n">
                        <div class="otx-anim-wrapper otx-anim-delay-0_2">n</div>
                    </div>
                    <div class="opal-text-letter otx-d">
                        <div class="otx-anim-wrapper otx-anim-delay-0_8">d</div>
                    </div>
                </div> -->
            </div>
        </div>
        <img class="smoke" src="@/assets/landing/purple.png" />
        <div class="lp-title-1">DEGENERATE</div>
        <div class="lp-title-2">APE ACADEMY</div>
    </div>
    <div class="landing-panel flex-center mobile apeintosh-panel-m">
        <div class="apeintosh-m" @click="goToAuction()">
            <div class="apeintosh-mobile-plate"></div>
            <img
                src="@/assets/landing/apeintosh.png"
                @load="() => (computerIsLoaded = true)"
            />
            <img class="apeintosh-reflect" src="@/assets/landing/screen.png" />
            <div class="apeintosh-screen"><HaramebayLogo /></div>
            <div class="apeintosh-shadow"></div>
        </div>
    </div>
</template>

<style>
@import '@/styles/base.css';

/* By default we build for mobile and expand outward. */

.apeintosh-mobile-plate {
    width: 300%;
    height: 150px;
    background-color: rgb(78, 15, 15);
    position: absolute;
    bottom: -22%;
    filter: blur(10px);
    border-radius: 50%;
}

.apeintosh-shadow {
    height: 17px;
    width: 100%;
    background-color: #000;
    position: absolute;
    bottom: -2%;
    filter: blur(6px);
    opacity: 0.5;
}

.apeintosh-screen {
    position: absolute;
    height: 47%;
    width: 76%;
    top: 11%;
    left: 12%;
    background-color: #b21515;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: inset 0px 0px 20px black;
}

.apeintosh img {
    height: 100%;
    z-index: 3;
}

.apeintosh-reflect {
    position: absolute;
    height: 46.5% !important;
    top: 10.8%;
    z-index: 1 !important;
    opacity: 0.5;
}

.apeintosh {
    height: 30%;
    z-index: 9;
    top: 24%;
    left: 22%;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: 0.25s filter ease;
    cursor: pointer;
}

.apeintosh-m img {
    height: 100%;
    z-index: 3;
}

.apeintosh-m {
    height: 300px;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    transition: 0.25s filter ease;
    cursor: pointer;
}

.apeintosh:hover {
    filter: brightness(1.2) contrast(1.5)
        drop-shadow(0 0 5px rgb(124, 181, 255)) saturate(0.4);
}

.transaction-buttons {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin-top: 40px;
}

.pb-transaction-button {
    height: 70px;
    width: 240px;
    background-color: #00000066;
    border-radius: 10px;
    border: 2px solid white;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 16px;
    font-size: 12px;
    cursor: pointer;
    transition: background-color 0.25s ease;
}

.pb-transaction-button:hover {
    background-color: #000000bb;
}

.transaction-text {
    margin-top: 50px;
    font-size: 22px;
}

.pb-title-icon {
    position: absolute;
    height: 70px;
    top: -40px;
}

.pb-title-icon img {
    height: 100%;
}

.pb-action-stat {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 180px;
}

.pb-action-stat-title {
    font-size: 12px;
    font-family: 'Gotham Medium', Helvetica, Arial;
    text-transform: uppercase;
}

.pb-action-stat-value {
    font-size: 24px;
}

.pb-action-button {
    height: 52px;
    width: 180px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #00000066;
    border-radius: 10px;
    border: 2px white solid;
    font-size: 13px;
    padding-top: 2px;
    margin-bottom: 50px;
    cursor: pointer;
    transition: background-color 0.25s ease;
}

.pb-action-button:hover {
    background-color: #000000bb;
}

.pb-action-container {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin-bottom: 30px;
}

.pb-contents {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    height: 80px;
    margin-top: 10px;
}

.pb-contents img {
    height: 100%;
    border-radius: 8px;
    margin: 0 4px;
}

.pb-contents-container {
    font-size: 11px;
    font-family: 'Gotham Medium', Helvetica, Arial;
    text-transform: uppercase;
    width: 100%;
    text-align: center;
    margin-bottom: 50px;
}

.pb-season-text {
    font-size: 10px;
    font-family: 'Gotham Medium', Helvetica, Arial;
    text-transform: uppercase;
    letter-spacing: 6px;
    line-height: 14px;
}

.pb-title-text {
    font-size: 20px;
}

.pb-title-container {
    width: 500px;
    height: 160px;
    border-radius: 18px;
    background-color: #00000066;
    border: 2px white solid;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-bottom: 60px;
}

.pb-modal-left {
    height: 420px;
}

.pb-modal-right {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-left: 100px;
}

.pb-modal-container {
    position: absolute;
    width: fit-content;
    height: fit-content;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    z-index: 8;
}

.pb-modal-transaction-status {
    position: absolute;
    width: fit-content;
    height: fit-content;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 8;
}

.pb-modal-backdrop {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 7;
}

.pb-modal {
    width: 100vw;
    height: 100vh;
    position: fixed;
    z-index: 10;
    background-color: #230000bb;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    transition: opacity 0.25s ease;
    display: flex;
    justify-content: center;
    align-items: center;
}

.opal-shadow {
    position: absolute;
    top: 150px;
    right: 124px;
    width: 140px;
    height: 3px;
    background: #00000099;
    border-radius: 50%;
}

.otx-shadow-wrapper {
    animation-duration: 2.66667s;
    animation-name: floating-shadow;
    animation-iteration-count: infinite;
    animation-timing-function: ease-in-out;
}

.floating {
    animation-name: floating;
    animation-duration: 8s;
    animation-iteration-count: infinite;
    animation-timing-function: ease-in-out;
}

.loader-container {
    top: 0;
    left: 0;
    z-index: 100;
    width: 100vw;
    height: 100vh;
    background: rgb(34, 1, 1);
    background: linear-gradient(
        180deg,
        rgba(34, 1, 1, 0.75) 49%,
        rgba(67, 11, 11, 0.75) 100%
    );
    backdrop-filter: blur(50px);
    -webkit-backdrop-filter: blur(50px);
    position: fixed;
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 1;
    transition: 0.5s opacity ease;
}

.is-loaded {
    opacity: 0;
    pointer-events: none;
}

.mobile-shadow {
    display: none;
}

.backend-assembly {
    position: absolute;
    width: 100vw;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
}

.opal-text-letter {
    display: inline-block;
    font-size: 35vw;
    letter-spacing: 1px;
    color: #bb89ff;
    filter: drop-shadow(0 0 8px #c9bfff);
}

.otx-anim-wrapper {
    animation-duration: 0.8s;
    animation-name: shake;
    animation-iteration-count: infinite;
    animation-direction: alternate;
}

.otx-anim-delay-0_2 {
    animation-delay: 0.2s;
}

.otx-anim-delay-0_4 {
    animation-delay: 0.4s;
}

.otx-anim-delay-0_6 {
    animation-delay: 0.6s;
}

.otx-anim-delay-0_8 {
    animation-delay: 0.8s;
}

.otx-anim-delay-1_0 {
    animation-delay: 1s;
}

.otx-anim-delay-1_2 {
    animation-delay: 1.2s;
}

.otx-a {
    font-size: 2.5vw;
    transform: rotate(-5deg);
}

.otx-s1 {
    font-size: 2vw;
    transform: translateY(-10px);
}

.otx-s2 {
    font-size: 3vw;
    transform: translateY(3px) rotate(2deg);
}

.otx-e {
    font-size: 2.5vw;
    transform: translateY(-6px);
}

.otx-n {
    font-size: 2.5vw;
    transform: translateY(-3px);
}

.otx-d {
    font-size: 2.5vw;
    transform: translateY(-2px) rotate(3deg);
}

.opal-text {
    position: fixed;
    z-index: 6;
    font-family: 'Magician', Arial, Helvetica, sans-serif;
    display: inline-block;
    pointer-events: none;
    opacity: 0;
    white-space: nowrap;
    margin-left: 4vw;
}

.show-opal-text {
    opacity: 1;
}

.opal-container {
    display: block;
    position: absolute;
    height: 10%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 5;
    bottom: 20%;
    filter: drop-shadow(-5px 0 10px black);
}

.opal-container img {
    height: 20vh;
    transition: 0.25s filter ease;
    cursor: pointer;
}

.opal-container img:hover {
    filter: brightness(1.2) contrast(1.5)
        drop-shadow(0 0 5px rgb(124, 181, 255)) saturate(0.4);
}

.ape-animation {
    position: absolute;
    height: 100%;
    z-index: 4;
}

.smoke {
    filter: blur(1px) hue-rotate(100deg);
    height: 100%;
    width: 100%;
    z-index: 0;
    position: absolute;
}

.landing-btn {
    margin: 0 0 50px 0;
    animation: shake-with-delay 4.72s ease infinite;
}

.landing-btn:hover {
    background-color: rgba(255, 255, 255, 0.2);
}

.landing-btn-container {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    height: 100%;
    width: 100%;
    z-index: 1;
}

.landing-panel {
    width: 100vw;
    height: 100vh;
    background: rgba(31, 28, 26);
    overflow: hidden;
    flex-direction: column;
}

.ape {
    position: absolute;
    width: 100%;
    height: auto;
    object-fit: cover;
    z-index: 3;
    min-width: 100%;
    min-height: 100%;
    margin-right: 85px;
}

.lp-title-1 {
    position: relative;
    font-family: 'DrukCond Super', Helvetica, Arial;
    font-size: 360px;
    font-weight: 400;
    line-height: 230px;
    text-align: center;
    color: #ff0a0a;
    text-shadow: -3px 4px 5px rgba(0, 0, 0, 0.25);
    opacity: 0.08;
}

.lp-title-2 {
    position: relative;
    font-family: 'DrukCond Super', Helvetica, Arial;
    font-size: 340px;
    font-weight: 400;
    line-height: 320px;
    text-align: center;
    color: #ff0a0a;
    text-shadow: -3px 4px 5px rgba(0, 0, 0, 0.25);
    opacity: 0.08;
    padding-bottom: 20px;
}

.apeintosh-panel-m {
    background: linear-gradient(#400c0c 0%, #400c0c 60%, #4e0f0f 100%);
    height: 100vh;
    z-index: 4;
}

.apeintosh-panel {
    background-color: #1a0c40;
    height: 100vh;
    z-index: 4;
}

/* HTML: <div class="loader"></div> */
.loader {
    width: 20px;
    aspect-ratio: 1;
    border-radius: 50%;
    background: #ff0d0d;
    display: grid;
    animation: l22-0 2s infinite linear;
}
.loader:before,
.loader:after {
    content: '';
    grid-area: 1/1;
    margin: 15%;
    border-radius: 50%;
    background: inherit;
    transform: rotate(0deg) translate(150%);
    animation: l22 1s infinite;
}
.loader:after {
    animation-delay: -0.5s;
}
.ldr-white {
    background: #ffffff;
}
.disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

.desktop {
    display: inherit;
}

.mobile {
    display: none !important;
}

@keyframes l22-0 {
    100% {
        transform: rotate(1turn);
    }
}
@keyframes l22 {
    100% {
        transform: rotate(1turn) translate(150%);
    }
}

@media only screen and (max-width: 992px) {
    .opal-shadow {
        display: none;
    }
    .mobile {
        display: flex !important;
    }
    .desktop {
        display: none;
    }
    .transaction-buttons {
        flex-direction: column;
    }
    .pb-transaction-button {
        margin: 12px 0;
    }
    .pb-title-icon {
        display: none;
    }
    .transaction-text {
        width: 80%;
        font-size: 24px;
        text-align: center;
    }
    .mobile-left {
        align-items: flex-start;
    }
    .mobile-right {
        align-items: flex-end;
    }
    .pb-modal {
        width: 100vw;
        height: 100vh;
        top: 0;
    }
    .pb-modal-container {
        flex-direction: column;
        width: 100%;
    }
    .pb-title-container {
        width: 100%;
        height: 80px;
        background-color: #00000000;
        border: none;
        margin-bottom: 10px;
    }
    .pb-action-container {
        width: 80%;
    }
    .pb-action-button {
        margin-bottom: 0;
    }
    .pb-modal-right {
        width: 100%;
        margin-left: 0;
    }
    .pb-modal-left {
        height: 200px;
        margin-top: 40px;
        margin-bottom: 20px;
    }
    .pb-contents {
        width: 100%;
        overflow-x: scroll;
    }
    .pb-contents-container {
        display: none;
    }
    .pack-button {
        display: flex;
    }
    .mobile-shadow {
        display: block;
        position: absolute;
        background-color: #00000044;
        width: 74%;
        height: 2%;
        margin-top: 104%;
        margin-left: 5%;
        border-radius: 50%;
        filter: blur(3px);
    }
    .backend-assembly {
        background: rgb(34, 1, 1);
        background: linear-gradient(180deg, #220101 49%, #430b0b);
    }
    .ape-animation {
        width: 250%;
    }
    .smoke {
        height: 100%;
        width: auto;
        display: none;
    }
    .opal-container {
        height: 80px;
        margin: 250px 0 0 0;
        bottom: initial;
    }
    .lp-title-1 {
        font-size: 130px;
        line-height: 120px;
        opacity: 0.1;
    }
    .lp-title-2 {
        font-size: 120px;
        line-height: 80px;
        padding-bottom: 0px;
        opacity: 0.1;
    }
}

@media only screen and (max-width: 768px) {
    .ape {
        min-width: auto;
        min-height: auto;
        height: auto;
        width: 245%;
        margin-right: 30px;
    }
}

/* These styles are applied when the aspect ratio of the browser
is greater than 2/1. */
@media (min-aspect-ratio: 2/1) {
    .opal-text {
        font-size: 50%;
        margin-left: 10vw;
    }
}

@keyframes shake-with-delay {
    0% {
        transform: translate(0, 0);
    }

    1.78571% {
        transform: translate(2px, 0);
    }

    3.57143% {
        transform: translate(0, 0);
    }

    5.35714% {
        transform: translate(4px, 0);
    }

    7.14286% {
        transform: translate(0, 0);
    }

    8.92857% {
        transform: translate(4px, 0);
    }

    10.71429% {
        transform: translate(-1px, 0);
    }

    12.57281% {
        transform: translate(1px, 0);
    }

    14.32619% {
        transform: translate(0, 0);
    }

    50% {
        transform: translate(0, 0);
    }
}

@keyframes floating {
    0% {
        transform: perspective(400px) rotateY(6deg) translate(0, 0px);
    }

    16.67% {
        transform: perspective(400px) rotateY(-6deg) translate(0, 10px);
    }

    33.34% {
        transform: perspective(400px) rotateY(6deg) translate(0, 0px);
    }

    50% {
        transform: perspective(400px) rotateY(-6deg) translate(0, 10px);
    }

    66.68% {
        transform: perspective(400px) rotateY(6deg) translate(0, 0px);
    }

    83.35% {
        transform: perspective(400px) rotateY(-6deg) translate(0, 10px);
    }

    100% {
        transform: perspective(400px) rotateY(6deg) translate(0, 0px);
    }
}

@keyframes floating-shadow {
    0% {
        filter: blur(8px);
    }

    50% {
        filter: blur(4px);
    }

    100% {
        filter: blur(8px);
    }
}

@keyframes shake {
    0% {
        transform: translate(0.5px, 0.5px) rotate(0deg);
    }
    10% {
        transform: translate(-0.5px, -0.5px) rotate(-1deg);
    }
    20% {
        transform: translate(-0.5px, 0px) rotate(1deg);
    }
    30% {
        transform: translate(0.5px, 0.5px) rotate(0deg);
    }
    40% {
        transform: translate(0.5px, -0.5px) rotate(1deg);
    }
    50% {
        transform: translate(-0.5px, 0.5px) rotate(-1deg);
    }
    60% {
        transform: translate(-0.5px, 0.5px) rotate(0deg);
    }
    70% {
        transform: translate(0.5px, 0.5px) rotate(-1deg);
    }
    80% {
        transform: translate(-0.5px, -0.5px) rotate(1deg);
    }
    90% {
        transform: translate(0.5px, 0.5px) rotate(0deg);
    }
    100% {
        transform: translate(0.5px, -0.5px) rotate(-1deg);
    }
}
</style>
